import { useMemo } from 'react';
import useUI from 'shared/hooks/use-ui';
import {
  ChainDispensary,
  UseChainDispensarySelectReturn,
  ToggleSelectedAll,
  HandleChange,
  CreateHeaderLabel,
  ToggleStateSelection,
  IsStateFullySelected,
  IsStatePartiallySelected,
  DispensariesByState,
} from './chain-dispensary-select.types';

type UseChainDispensarySelectOptions = {
  /** If true, allows deselecting all items. If false, keeps activeDispensaryId selected. Default: false */
  allowEmptySelection?: boolean;
};

/**
 * Groups dispensaries by state and returns a Map sorted alphabetically by state name.
 */
function groupDispensariesByState(dispensaries: ChainDispensary[]): DispensariesByState {
  const stateMap = new Map<string, ChainDispensary[]>();

  dispensaries.forEach((dispensary) => {
    const state = dispensary.state ?? 'Unknown';
    const existing = stateMap.get(state) ?? [];
    stateMap.set(state, [...existing, dispensary]);
  });

  // Sort by state name alphabetically
  const sortedEntries = Array.from(stateMap.entries()).sort(([a], [b]) => a.localeCompare(b));
  return new Map(sortedEntries);
}

export function useChainDispensarySelect(
  dispensaries: ChainDispensary[],
  selectedDispensaryIds: string[],
  setSelectedDispensaryIds: (selectedDispensaryIds: string[]) => void,
  options: UseChainDispensarySelectOptions = {}
): UseChainDispensarySelectReturn {
  const { allowEmptySelection = false } = options;
  const UI = useUI();
  const { dispensary } = UI;
  const activeDispensaryId = dispensary.id ?? '';
  const hasAllSelected = selectedDispensaryIds.length === dispensaries.length && dispensaries.length > 0;

  // Group dispensaries by state
  const dispensariesByState = useMemo(() => groupDispensariesByState(dispensaries), [dispensaries]);

  const toggleSelectedAll: ToggleSelectedAll = (e) => {
    e.stopPropagation();
    if (hasAllSelected) {
      // Deselect all - either empty or keep active dispensary based on option
      setSelectedDispensaryIds(allowEmptySelection ? [] : [activeDispensaryId]);
    } else {
      // Select all
      setSelectedDispensaryIds(dispensaries.map((d) => d.id));
    }
  };

  /**
   * Checks if all dispensaries in a given state are selected.
   */
  const isStateFullySelected: IsStateFullySelected = (state: string) => {
    const stateDispensaries = dispensariesByState.get(state) ?? [];
    if (stateDispensaries.length === 0) {
      return false;
    }
    return stateDispensaries.every((d) => selectedDispensaryIds.includes(d.id));
  };

  /**
   * Checks if some (but not all) dispensaries in a given state are selected.
   */
  const isStatePartiallySelected: IsStatePartiallySelected = (state: string) => {
    const stateDispensaries = dispensariesByState.get(state) ?? [];
    if (stateDispensaries.length === 0) {
      return false;
    }
    const selectedCount = stateDispensaries.filter((d) => selectedDispensaryIds.includes(d.id)).length;
    return selectedCount > 0 && selectedCount < stateDispensaries.length;
  };

  /**
   * Toggles selection of all dispensaries in a given state.
   * If all are selected, deselects them. Otherwise, selects all.
   */
  const toggleStateSelection: ToggleStateSelection = (state: string) => {
    const stateDispensaries = dispensariesByState.get(state) ?? [];
    const stateDispensaryIds = stateDispensaries.map((d) => d.id);
    const isFullySelected = isStateFullySelected(state);

    if (isFullySelected) {
      // Deselect all dispensaries in this state
      let newSelection = selectedDispensaryIds.filter((id) => !stateDispensaryIds.includes(id));

      // If empty selection not allowed, ensure active dispensary stays selected
      if (!allowEmptySelection && !newSelection.includes(activeDispensaryId)) {
        newSelection = [activeDispensaryId, ...newSelection];
      }

      setSelectedDispensaryIds(newSelection);
    } else {
      // Select all dispensaries in this state
      const newSelection = [...new Set([...selectedDispensaryIds, ...stateDispensaryIds])];
      setSelectedDispensaryIds(newSelection);
    }
  };

  const handleChange: HandleChange = (e) => {
    // Material-UI Select can include null/undefined from MenuItems without value props
    const allSelectedIds = e.target.value as (string | null | undefined)[];

    // Filter out null/undefined values that Material-UI Select may add from MenuItems without value props
    const validIds = allSelectedIds.filter((id): id is string => id != null);

    // If empty selection not allowed, ensure active dispensary stays selected
    if (!allowEmptySelection) {
      const hasActiveDispensary = validIds.includes(activeDispensaryId);
      if (!hasActiveDispensary) {
        setSelectedDispensaryIds([activeDispensaryId, ...validIds]);
        return;
      }
    }

    setSelectedDispensaryIds(validIds);
  };

  const createStickyHeaderLabel: CreateHeaderLabel = (selected) =>
    selected.length ? `${selected.length} Selected` : 'Select locations';

  const currentRetailerIds = new Set(dispensaries.map((el) => el.id));
  const hasStaleChainLocations = !selectedDispensaryIds.every((id) => currentRetailerIds.has(id));
  const selectedChainRetailerIds = hasStaleChainLocations
    ? selectedDispensaryIds.filter((id) => currentRetailerIds.has(id))
    : selectedDispensaryIds;

  return {
    createStickyHeaderLabel,
    handleChange,
    hasAllSelected,
    hasStaleChainLocations,
    selectedChainRetailerIds,
    selectedDispensaryIds,
    toggleSelectedAll,
    toggleStateSelection,
    isStateFullySelected,
    isStatePartiallySelected,
    dispensariesByState,
  };
}
